// # @maru/protocol — 서버 규약의 단일 진실
//
// 여기 있는 타입과 상수는 **러스트의 `crates/gateway/src/proto.rs` 와 한 글자도
// 달라서는 안 된다.** 달라지는 순간 클라이언트는 조용히 틀린 동기화를 한다 —
// 예외도 안 나고, 그냥 메시지 몇 개가 사라진다.
//
// 그래서 `러스트와맞춘다.test.ts` 가 러스트 소스를 직접 읽어 비교한다.
// 문서로 "맞춰 주세요" 라고 적는 것과는 다른 종류의 보장이다.
//
// ## 왜 코드 생성을 하지 않는가
// 생성기는 생성기대로 유지보수 대상이 되고, 생성 결과가 커밋됐는지 아닌지로
// 리뷰가 지저분해진다. 표면이 이 정도로 작을 때는 **손으로 쓰고 시험으로 묶는 편**이
// 읽기도 고치기도 낫다. 표면이 커지면 그때 생성기로 옮긴다.

import { blocks_of, inline_of } from './markup.ts'

export {
  apply_format,
  blocks_of,
  continue_list,
  type Edit,
  type Format,
  format_of_key,
  formats_at,
  in_fence,
  inline_of,
  type MarkLine,
  type MarkPiece,
  type MarkupNames,
  marks_of,
  markup_of,
  on_enter,
} from './markup.ts'

export const PROTO_VERSION = 1

/** 이보다 오래 조용하면 서버가 연결을 닫는다. */
export const DEFAULT_HEARTBEAT_SECS = 30

/** 서버가 참아 주는 침묵. 맥박 간격의 2배보다 넉넉해야 한다. */
export const PATIENCE_SECS = 75

/** 갭을 한 번에 메우는 크기. REST 한 요청의 상한과 같다. */
export const BACKFILL_CHUNK = 200

/** 찾아보기·보관함 한 쪽의 상한(`maru_ports::store::CHANNEL_PAGE_MAX`). 더 달라고 해도 서버가 자른다. */
export const CHANNEL_PAGE_MAX = 1000

/** 조직원 목록 한 쪽의 상한(`maru_app::member::PAGE_MAX`). `limit` 이 없을 때의 쪽 크기이기도 하다. */
export const MEMBER_PAGE_MAX = 1000

/** 나중에 보기 목록의 상한(`SAVED_MAX`). 최근 담은 순으로 이만큼만 온다. */
export const SAVED_MAX = 1000

/** 커스텀 이모지 목록의 상한(`EMOJI_MAX`). 이름순 앞에서부터. */
export const EMOJI_MAX = 5000

/** 허들에 "아직 있다" 를 알리는 간격. 서버는 이것의 세 배 동안 소식이 없으면 뺀다. */
export const HUDDLE_BEAT_SECS = 15

export type ChannelId = number & { readonly __brand: 'Channel' }
export type UserId = number & { readonly __brand: 'user' }
/** 사용자 그룹(`@팀이름`). 사람과 같은 자리에서 불리지만 사람이 아니다. */
export type GroupId = number & { readonly __brand: 'group' }

/**
 * 채널 안에서 1,2,3… 으로 **빈틈없이** 올라가는 번호.
 *
 * 브랜드 타입이라 그냥 `number` 를 넣을 수 없다. 사소해 보이지만,
 * seq 자리에 timestamp 를 넣는 실수는 실제로 일어나고 **런타임에는 안 잡힌다.**
 */
export type Seq = number & { readonly __brand: 'seq' }

export const seq = (n: number): Seq => n as Seq
export const channel = (n: number): ChannelId => n as ChannelId
export const user = (n: number): UserId => n as UserId

export const FIRST: Seq = seq(0)

/**
 * 보내는 쪽이 붙이는 한 줄의 신분증(UUID).
 *
 * 재시도할 때 **같은 값**을 다시 보내야 서버가 같은 줄임을 알아본다. 그래서
 * 이 값은 전송 함수가 아니라 화면이 만들어 붙든다 — 함수 안에서 만들면
 * 재시도마다 새 값이 되어 같은 말이 두 번 붙는다.
 */
export type ClientMsgId = string & { readonly __brand: 'client_msg' }

export const fresh_msg_id = (): ClientMsgId => crypto.randomUUID() as ClientMsgId

export type ChannelCursor = { channel: ChannelId; have: Seq }
export type FillGap = { channel: ChannelId; since: Seq; upto: Seq }

// ── 본문 ──────────────────────────────────────────────────────────────────
//
// 본문은 문자열이 아니라 **블록 트리**다. 마크다운 원문을 저장하면 검색·멘션·미리보기를
// 붙일 때마다 다시 파싱해야 하고, 클라이언트마다 파서가 달라 화면이 어긋난다.
// 파싱은 쓰기 한 번, 읽기는 그리기만(`crates/domain/src/message.rs` 와 같은 모양이다).

/** 본문 글자 수 상한. 러스트 `BODY_MAX` 와 같아야 한다. */
export const BODY_MAX = 4000

export type Span =
  | { t: 'text'; text: string; bold: boolean; italic: boolean; strike: boolean; code: boolean }
  | { t: 'mention_user'; user: UserId }
  | { t: 'mention_group'; group: GroupId }
  | { t: 'mention_all'; here: boolean }
  /** `no_card` — 글쓴이가 카드를 뗐다(`POST …/hide-card`). 러스트는 참일 때만 싣는다. */
  | { t: 'link'; url: string; view: string | null; no_card?: boolean }
  | { t: 'emoji'; name: string }

export type Block =
  | { t: 'para'; spans: Span[] }
  | { t: 'code'; lang: string | null; code: string }
  | { t: 'quote'; spans: Span[] }
  | { t: 'list'; ordered: boolean; entries: Span[][] }
  | { t: 'divider' }
  /** 첨부 한 개. 이름·종류·주소는 `GET /v1/attachments/{id}` 가 준다. */
  | { t: 'attachment'; id: string }
  // 아래 넷은 **앱만 쓰는 레이아웃 블록**(슬랙 Block Kit). 입력칸은 만들지 않는다 —
  // 사람이 보내면 서버가 409 로 막는다(`app::send_message::may_write`).
  /** 굵은 제목 한 줄, 서식 없음(150자). */
  | { t: 'header'; text: string }
  /** 글 + 둘씩 나란히 서는 칸(최대 10). 곁붙이 단추·고르기가 하나 붙을 수 있다. */
  | {
      t: 'section'
      spans: Span[]
      fields?: Span[][]
      accessory?: Element | null
      id?: string | null
    }
  /** 바깥 그림. 주소는 서버가 절대 https 만 통과시킨다. `alt` 는 필수. */
  | { t: 'image'; url: string; alt: string; title?: string | null }
  /** 작고 옅은 덧말. */
  | { t: 'context'; spans: Span[] }
  /** 단추·고르기 줄(최대 25). 누르면 글을 쓴 앱에게 `block_actions` 가 간다. */
  | { t: 'actions'; elements: Element[]; id?: string | null }

/**
 * 누르거나 고르는 것(슬랙 `button`·`static_select`). 러스트 `domain::element::Element`.
 * 누를 때 보내는 것은 블록 자리·`action`·고른 값뿐이다 — 값은 서버가 글에서 다시 읽는다.
 */
export type Element =
  | {
      t: 'button'
      action: string
      text: string
      value?: string | null
      style?: 'primary' | 'danger' | null
      /** 누르면 이 주소를 연다. 그래도 앱에게 알린다. */
      url?: string | null
    }
  | { t: 'select'; action: string; placeholder: string; options: { text: string; value: string }[] }

/**
 * 앱이 띄우는 창(슬랙 모달, `views.open`). 러스트 `domain::view::View`.
 * 블록은 입력칸이거나 글의 레이아웃 블록 그대로다. 창 안에는 단추·첨부가 없다(서버가 막는다).
 */
export interface View {
  title: string
  submit?: string | null
  close?: string | null
  blocks: ViewBlock[]
  callback_id?: string | null
  private_metadata?: string | null
  notify_on_close?: boolean
}

export type ViewBlock = InputBlock | Block

/** 입력칸 하나(슬랙 `input` 블록). `id` 가 없으면 자리 번호(`b3`)가 칸 이름이다. */
export interface InputBlock {
  t: 'input'
  id?: string | null
  label: string
  optional?: boolean
  hint?: string | null
  element: Field
}

/** 입력칸의 요소(슬랙 `plain_text_input`·`static_select`). 러스트 `domain::view::Field`. */
export type Field =
  | {
      t: 'text'
      action: string
      multiline?: boolean
      placeholder?: string | null
      initial?: string | null
      min_length?: number | null
      max_length?: number | null
    }
  | {
      t: 'select'
      action: string
      placeholder: string
      options: { text: string; value: string }[]
      initial?: string | null
    }

/** 창의 칸 이름. 서버(`Input::block_id`)와 같은 규칙이다. */
export const input_id = (b: InputBlock, index: number): string => b.id ?? `b${index}`

/** 첨부 한 개의 상한(바이트). 러스트 `attachment::MAX_BYTES` 와 같다. 넘기면 올릴 자리를 안 준다. */
export const ATTACHMENT_MAX_BYTES = 100 * 1024 * 1024

/** 얼굴 사진 한 장의 상한(바이트). 러스트 `picture::MAX_BYTES` 와 같다. 화면은 256px 로 줄여 올린다. */
export const FACE_MAX_BYTES = 1024 * 1024

/** 얼굴 사진으로 받는 종류. 러스트 `picture::KINDS` 와 같다 — SVG 는 없다. */
export const FACE_KINDS = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const

/** 본문 전체. 빈 본문은 보내지 않는다. */
export type Body = Block[]

export const plain = (text: string): Span => ({
  t: 'text',
  text,
  bold: false,
  italic: false,
  strike: false,
  code: false,
})

/**
 * 사람이 친 글을 본문으로.
 *
 * 입력칸의 서식 표기(`*굵게*` `_기울임_` `~취소선~` `` `코드` ``, `> ` 인용, `- `·`1. ` 목록,
 * ```` ``` ```` 코드 블록)를 블록으로 푼다(`markup.ts`). 서버는 블록만 받으므로 파서는
 * 여기 하나다. 코드 안의 주소·`@이름` 은 글자로 남는다.
 */
export function body_of(written: string, resolve?: Resolve): Body {
  return blocks_of(written, (text) => {
    const spans = inline_of(text).flatMap((s): Span[] => {
      if (s.code) return [s]
      const { t: _t, text: _x, ...flags } = s
      return linkify(s.text).flatMap((x) => (x.t === 'text' ? emojify({ ...x, ...flags }) : [x]))
    })
    return resolve === undefined ? spans : mentionize(spans, resolve)
  })
}

/**
 * 글자 속 `:이름:` 을 이모지 조각으로(3차 F131). 이름 규칙은 커스텀 이모지와 같고(영문·숫자·`-`·`_`,
 * 32자까지 — `domain/custom_emoji.rs`) 표준 이름의 `+1` 을 더 받는다. 글자가 하나도 없는 이름(`10:30:45` 의
 * `30`)은 시각이지 이모지가 아니다. 무슨 그림인지는 그리는 쪽이 정한다 — 모르면 `:이름:` 그대로 보인다.
 */
export function emojify(span: Extract<Span, { t: 'text' }>): Span[] {
  const out: Span[] = []
  let at = 0
  for (const m of span.text.matchAll(/:([\w+-]{1,32}):/g)) {
    const name = (m[1] ?? '').toLowerCase()
    if (!/^[a-z0-9_+-]+$/.test(name) || !/[a-z]|^[+-]1$/.test(name)) continue
    if (m.index > at) out.push({ ...span, text: span.text.slice(at, m.index) })
    out.push({ t: 'emoji', name })
    at = m.index + m[0].length
  }
  if (at === 0) return [span]
  if (at < span.text.length) out.push({ ...span, text: span.text.slice(at) })
  return out
}

/** 이름이 그룹을 가리킬 때. 사람 번호와 섞이지 않게 감싸서 돌려준다. */
export interface GroupRef {
  group: GroupId
}

/** 이름 → 사람 또는 그룹. 모르는 이름이면 `undefined` — 그 `@이름` 은 글자로 남는다. */
export type Resolve = (name: string) => UserId | GroupRef | undefined

/**
 * 글자 조각 속의 `@이름` 을 호출 조각으로 바꾼다. `@channel`·`@here` 는 방 전체다.
 * 이름은 공백·`@` 를 품지 않는다 — 입력칸의 자동완성이 그 규칙으로 넣는다.
 * 링크 조각은 건드리지 않는다(주소 속 `@` 는 사람이 아니다).
 */
export function mentionize(spans: readonly Span[], resolve: Resolve): Span[] {
  return spans.flatMap((span) =>
    span.t === 'text' && !span.code && span.text.includes('@')
      ? split_mentions(span, resolve)
      : [span],
  )
}

function split_mentions(span: Extract<Span, { t: 'text' }>, resolve: Resolve): Span[] {
  const out: Span[] = []
  let at = 0
  for (const m of span.text.matchAll(/(^|\s)@([^\s@]+)/g)) {
    const name = m[2] ?? ''
    const hit = mention_of(name, resolve)
    if (hit === null) continue
    const start = m.index + (m[1]?.length ?? 0)
    if (start > at) out.push({ ...span, text: span.text.slice(at, start) })
    out.push(hit)
    at = start + 1 + name.length
  }
  if (at < span.text.length) out.push({ ...span, text: span.text.slice(at) })
  return out
}

function mention_of(name: string, resolve: Resolve): Span | null {
  if (name === 'channel') return { t: 'mention_all', here: false }
  if (name === 'here') return { t: 'mention_all', here: true }
  const hit = resolve(name)
  if (hit === undefined) return null
  return typeof hit === 'number'
    ? { t: 'mention_user', user: hit }
    : { t: 'mention_group', group: hit.group }
}

/**
 * 글 속의 `http(s)://…` 만 링크 조각으로 뗀다. 서식 해석이 아니라 **주소 찾기**다 —
 * 서버가 링크 카드를 만들려면 조각이 링크여야 하고, 화면이 누를 수 있어야 한다.
 * 끝의 문장부호(`.,)!?`)는 주소가 아니다.
 */
export function linkify(text: string): Span[] {
  const out: Span[] = []
  let at = 0
  // `<주소|글자>` 는 글자에 건 링크(슬랙 표기와 같다, 3차 F72). 주소는 여기서도 http(s) 만이다.
  for (const m of text.matchAll(/<(https?:\/\/[^\s|<>]+)\|([^<>\n]+)>|https?:\/\/[^\s<>"']+/g)) {
    if (m.index > at) out.push(plain(text.slice(at, m.index)))
    if (m[1] !== undefined && m[2] !== undefined) {
      const view = m[2].trim()
      out.push({ t: 'link', url: m[1], view: view === '' ? null : view })
      at = m.index + m[0].length
      continue
    }
    let url = m[0]
    while (/[.,;:!?)\]]$/.test(url)) url = url.slice(0, -1)
    out.push({ t: 'link', url, view: null })
    at = m.index + url.length
  }
  if (at < text.length) out.push(plain(text.slice(at)))
  return out.length === 0 ? [plain(text)] : out
}

/**
 * 본문에 든 링크 주소(중복 없이, 순서대로). 카드를 물을 때 쓴다. 코드 블록 안은 뺀다.
 * 글쓴이가 카드를 뗀 링크(`no_card`)도 뺀다 — 묻지도 그리지도 않는다.
 */
export function links_of(body: Body): string[] {
  const urls = body
    .flatMap(span_lists)
    .flat()
    .flatMap((s) => (s.t === 'link' && s.no_card !== true ? [s.url] : []))
  return [...new Set(urls)]
}

/** 글줄이 든 블록의 span 목록들. 코드·구분선·첨부는 없다. */
function span_lists(b: Block): Span[][] {
  switch (b.t) {
    case 'para':
    case 'quote':
    case 'context':
      return [b.spans]
    case 'section':
      return [b.spans, ...(b.fields ?? [])]
    case 'list':
      return [...b.entries]
    default:
      return []
  }
}

/** 본문에 든 첨부 id 들. */
export function attachments_of(body: Body): string[] {
  return body.flatMap((b) => (b.t === 'attachment' ? [b.id] : []))
}

/** 알림·미리보기에 쓸 평문 한 줄. 그리기는 UI 가 한다. */
export function text_of(body: Body): string {
  return body
    .map(block_text)
    .filter((line) => line.length > 0)
    .join('\n')
}

function block_text(block: Block): string {
  switch (block.t) {
    case 'para':
    case 'quote':
      return spans_text(block.spans)
    case 'code':
      return block.code
    case 'list':
      return block.entries.map(spans_text).join(' · ')
    case 'divider':
      // 구분선은 평문에 남길 것이 없다. 빈 줄로 남기면 미리보기 앞이 비어 보인다.
      return ''
    case 'attachment':
      return '📎'
    case 'header':
      return block.text
    case 'section':
      return [block.spans, ...(block.fields ?? [])].map(spans_text).join(' · ')
    case 'image':
      return block.alt
    case 'context':
      return spans_text(block.spans)
    // 단추 글자는 글이 아니다 — 러스트 `span_chars` 와 같다.
    case 'actions':
      return ''
  }
}

function spans_text(spans: Span[]): string {
  return spans.map(span_text).join('')
}

function span_text(span: Span): string {
  switch (span.t) {
    case 'text':
      return span.text
    case 'mention_user':
      return `@${span.user}`
    case 'mention_group':
      return `@${span.group}`
    case 'mention_all':
      return span.here ? '@here' : '@channel'
    case 'link':
      return span.view ?? span.url
    case 'emoji':
      return `:${span.name}:`
  }
}

export type Inbound =
  | { op: 'resume'; have: ChannelCursor[] }
  | { op: 'subscribe'; channel: ChannelId }
  | { op: 'unsubscribe'; channel: ChannelId }
  | { op: 'typing'; channel: ChannelId }
  /**
   * 캔버스를 보고 있다 — 커서·선택 자리(UTF-16 글자 번호). `at` 이 `null` 이면 칸을 아직 안 눌렀다
   * (보기만 한다). `left` 면 판을 닫았다.
   */
  | { op: 'canvas_here'; channel: ChannelId; at: number | null; end: number | null; left?: boolean }
  | { op: 'heartbeat' }

/**
 * 소켓으로 밀려오는 사건. 러스트 `bus::Event` 와 같은 모양이고, 태그는 **`t`** 다
 * (`#[serde(tag = "t")]`). `kind` 로 읽으면 어떤 갈래에도 안 걸려 조용히 버려진다 —
 * 그게 한동안 실제로 일어난 버그였다. `matches_rust.test.ts` 가 태그 이름을 지킨다.
 */
export type Event =
  | { t: 'message'; channel: ChannelId; seq: Seq; author: UserId; by_bot?: boolean }
  | { t: 'edited'; channel: ChannelId; seq: Seq }
  | { t: 'deleted'; channel: ChannelId; seq: Seq }
  | { t: 'typing'; channel: ChannelId; who: UserId }
  | { t: 'read_state'; channel: ChannelId; who: UserId; upto: Seq }
  /** 바뀌었을 때만 온다. 수는 싣지 않는다 — 각자 제 화면을 ±1 하고 확신이 필요하면 다시 센다. */
  | { t: 'reacted'; channel: ChannelId; seq: Seq; who: UserId; emoji: string; added: boolean }
  /** 채널 구독과 무관하게 **나에게만** 온다. */
  | { t: 'mentioned'; channel: ChannelId; seq: Seq; who: UserId }
  /** 따르는 스레드에 답글이 달렸다. **나에게만** 온다. `root` 는 스레드 뿌리의 seq. */
  | { t: 'thread_replied'; channel: ChannelId; seq: Seq; root: Seq; who: UserId }
  /**
   * 남이 내 글에 반응을 달았다. **글쓴이에게만** 온다. 같은 글에 잇따른 반응은 서버가 묶어 한 번만 보낸다
   * (내 반응·알림 끈 채널은 오지 않는다). 수 갱신은 `reacted` 가 따로 한다.
   */
  | {
      t: 'reaction_received'
      channel: ChannelId
      seq: Seq
      who: UserId
      actor: UserId
      emoji: string
    }
  /** 당사자에게만 온다. 사이드바에 방이 나타나게 하는 것이 전부다. */
  | { t: 'membership'; channel: ChannelId; who: UserId; joined: boolean }
  /** 공개↔비공개 전환. 비공개가 되면 곧이어 `needs_resync` 가 오고, 재개가 구성원 채널만 다시 구독한다. */
  | { t: 'kind_changed'; channel: ChannelId; private: boolean }
  // 러스트의 `UserDisabled` 는 여기 없다 — 소켓으로 나가지 않는다. 게이트웨이가 알리지 않고 1008 로 닫는다.
  /**
   * 앱이 **나에게만** 보인 글(슬랙 `chat.postEphemeral`). 서버는 저장하지 않는다 — 본문이
   * 사건에 실려 오는 유일한 경우이고, 새로 고치면 사라진다.
   */
  | {
      t: 'ephemeral'
      channel: ChannelId
      who: UserId
      author: UserId
      parent?: Seq | null
      body: Body
    }
  /** 앱이 **내 앞에** 창을 열었다. 내용은 `GET /v1/views/{view}` 로 받는다. */
  | { t: 'view_opened'; channel: ChannelId; who: UserId; view: number }
  /**
   * 누가 이 채널의 허들에 들어왔거나 나왔다. 명단은 싣지 않는다 — `GET /v1/huddles` 로
   * 다시 받는다. 맥박이 끊겨 조용히 빠진 사람은 사건이 없다.
   */
  | { t: 'huddle'; channel: ChannelId; who: UserId; joined: boolean }
  /**
   * 채널 캔버스가 바뀌었다. 조각은 싣지 않는다 — 연 화면이 `GET …/canvas?since=` 로 받는다.
   * `upto` 가 없으면 제목·만들기·지우기 같은 머리의 변화다.
   */
  | { t: 'canvas_changed'; channel: ChannelId; upto?: number | null; by: UserId }
  /** 채널 이름·주제·보관이 바뀌었다. **테넌트 전원에게** 온다 — 채널 목록을 다시 받는다. */
  | { t: 'channel_changed'; channel: ChannelId }
  /** 핀이 꽂혔거나 빠졌다. 채널 구독자에게 온다. */
  | { t: 'pins_changed'; channel: ChannelId; seq: Seq; pinned: boolean }
  /** 채널 북마크가 바뀌었다. 채널 구독자에게 온다. */
  | { t: 'bookmarks_changed'; channel: ChannelId }
  /**
   * 내 초안이 적히거나 지워졌다. **내 기기들에게만** 온다. 본문은 없다 — `GET /v1/drafts` 로 받는다.
   * `origin` 이 이 기기의 꼬리표면 제가 쓴 것이니 버린다.
   */
  | {
      t: 'draft_changed'
      channel: ChannelId
      who: UserId
      thread?: Seq | null
      origin?: string | null
    }
  | { t: 'poll_changed'; channel: ChannelId; seq: Seq }
  // 아래는 테넌트 전체 사건이다 — **`channel` 이 없다.** 채널로 가르는 곳은 먼저 `has_channel` 로 거른다.
  /** 커스텀 이모지가 바뀌었다. */
  | { t: 'emoji_changed' }
  /** 사용자 그룹이 바뀌었다. */
  | { t: 'groups_changed' }
  /** 한 사람의 이름·프로필·얼굴·역할이 바뀌었다. */
  | { t: 'member_changed'; who: UserId }

  /**
   * 누가 채널 캔버스를 열고 어디를 보고 있다(커서·선택). 저장소에 닿지 않는 알림이라 잃어도 된다 —
   * 여는 화면이 맥박처럼 다시 보낸다. `at` 이 없으면 보기만 한다. `left` 면 판을 닫았다.
   */
  | {
      t: 'canvas_here'
      channel: ChannelId
      who: UserId
      at?: number | null
      end?: number | null
      left?: boolean
    }

/** 채널이 없는 테넌트 전체 사건. */
export type TenantEvent = Extract<
  Event,
  { t: 'emoji_changed' | 'groups_changed' | 'member_changed' }
>
/** 채널에 달린 사건. */
export type ChannelEvent = Exclude<Event, TenantEvent>

/** 채널에 달린 사건인가. 테넌트 전체 사건은 `channel` 이 없다. */
export function has_channel(e: Event): e is ChannelEvent {
  return 'channel' in e
}

/** 새 줄만 커서를 민다. 나머지는 seq 가 있어도 **이미 있는 줄에 대한 말**이다. */
export const is_new_line = (e: Event): e is Extract<Event, { t: 'message' }> => e.t === 'message'

export type Outbound =
  | { op: 'greeting'; proto: number; heartbeat_secs: number; me: UserId }
  | { op: 'sync'; to_fill: FillGap[] }
  /**
   * 사건은 **펼쳐서** 온다 — 러스트 `Outbound::Event(Event)` 는 `op` 태그 안의 newtype 이라
   * serde 가 `{"op":"event","t":"message",…}` 로 쓴다. `event` 칸은 없다.
   */
  | ({ op: 'event' } & Event)
  | { op: 'needs_resync'; channel: ChannelId }
  | { op: 'heartbeat_ack' }
  | { op: 'fail'; reason: string }

/** 들어온 seq 를 어떻게 다룰지. 러스트 `seq::MailVerdict` 과 같은 판단이다. */
export type MailVerdict =
  | { kind: 'apply' }
  | { kind: 'already_had' }
  | { kind: 'gap'; since: Seq; upto: Seq }

/**
 * ## 이 함수가 클라이언트 동기화의 전부다
 *
 * - 바로 다음 번호면 적용한다
 * - 이미 가진 것보다 작으면 버린다(재전송·순서 뒤바뀜)
 * - 건너뛰었으면 **그 사이를 REST 로 메운다.** 건너뛴 것을 그냥 적용하면
 *   타임라인에 영원히 메워지지 않는 구멍이 남는다.
 */
export function verdict(have: Seq, incoming: Seq): MailVerdict {
  if (incoming <= have) return { kind: 'already_had' }
  if (incoming === have + 1) return { kind: 'apply' }
  return { kind: 'gap', since: seq(have + 1), upto: seq(incoming - 1) }
}

/**
 * 갭을 한 요청에 담을 수 있는 크기로 쪼갠다.
 *
 * 넘치면 **최신 쪽을 남긴다.** 사람은 방금 온 메시지를 먼저 보고 싶어 하지,
 * 3주 전 것을 먼저 보고 싶어 하지 않는다.
 */
export function backfill_plan(gap: FillGap, MAX_CHUNKS: number): FillGap[] {
  const capacity = BACKFILL_CHUNK * MAX_CHUNKS
  const whole = gap.upto - gap.since + 1
  const since = whole > capacity ? seq(gap.upto - capacity + 1) : gap.since

  const plan: FillGap[] = []
  for (let s = since; s <= gap.upto; s = seq(s + BACKFILL_CHUNK)) {
    plan.push({
      channel: gap.channel,
      since: s,
      upto: seq(Math.min(s + BACKFILL_CHUNK - 1, gap.upto)),
    })
  }
  return plan
}

/** 안읽음은 **뺄셈**이다. 세지 않는다. */
export function unread(channel_last: Seq, read_upto: Seq): number {
  return Math.max(0, channel_last - read_upto)
}

/**
 * 앱이 구독하는 사건 이름(슬랙 Events API). 러스트 `domain::app_event::EventType` 과 같아야
 * 한다 — `matches_rust.test.ts` 가 본다. 수정·삭제는 `message` 의 하위 종류라 따로 없다.
 */
export const APP_EVENT_TYPES = [
  'message',
  'app_mention',
  'reaction_added',
  'reaction_removed',
  'member_joined_channel',
  'member_left_channel',
] as const
export type AppEventType = (typeof APP_EVENT_TYPES)[number]

/**
 * 앱이 요청할 수 있는 권한(스코프). 러스트 `domain::scope::Scope::as_str` 과 같은 순서·이름이다
 * (`matches_rust.test.ts`). 동의 화면과 앱 등록 칸이 이 목록을 쓴다 — 화면이 짐작한 이름으로
 * 적었더니 동의 화면에 있지도 않은 권한이 한국어로, 진짜 권한은 영어 원문으로 섰다(3차 F152).
 */
export const APP_SCOPES = ['messages:read', 'messages:write', 'channels:read'] as const
export type AppScope = (typeof APP_SCOPES)[number]

/**
 * 감사 로그에 적히는 관리 행동. 러스트 `domain::audit::Action::as_str` 과 같은 순서·이름이다
 * (`matches_rust.test.ts`). 옛 줄에는 여기 없는 이름이 있을 수 있어 읽는 쪽은 `string` 으로 받는다.
 */
export const AUDIT_ACTIONS = [
  'role_changed',
  'user_deactivated',
  'user_reactivated',
  'two_factor_reset',
  'invite_sent',
  'invite_revoked',
  'app_created',
  'app_deleted',
  'app_uninstalled',
  'webhook_created',
  'webhook_deleted',
  'outgoing_created',
  'outgoing_deleted',
  'command_created',
  'command_deleted',
  'retention_changed',
  'export_started',
  'security_changed',
  'channel_kind_changed',
  'channel_archived',
  'channel_unarchived',
  'channel_manager_added',
  'channel_manager_removed',
  'sso_changed',
  'sso_removed',
  'scim_token_created',
  'scim_token_revoked',
  'scim_user_created',
  'connect_invited',
  'connect_accepted',
  'connect_removed',
  'connect_policy_changed',
  'connect_dm_invited',
  'connect_dm_accepted',
  'connect_dm_declined',
  'ai_changed',
  'user_login',
  'invite_link_created',
  'invite_link_revoked',
  'channel_deleted',
  'app_allowed',
  'app_disallowed',
] as const
export type AuditAction = (typeof AUDIT_ACTIONS)[number]

/**
 * 공유 초대 링크(G30)의 고를 수 있는 수명(일)과 조직당 살아 있는 링크 수. 러스트
 * `domain::invite_link::{DAYS, ALIVE_MAX}` 와 같은 값이다(`matches_rust.test.ts`).
 */
export const INVITE_LINK_DAYS = [1, 7, 30] as const
export const INVITE_LINKS_ALIVE_MAX = 5

/**
 * SSO 를 얼마나 세게 거는가. 러스트 `domain::sso::SsoMode::as_str` 과 같은 순서·이름이다
 * (`matches_rust.test.ts`). 소유자는 어느 모드에서도 비밀번호·메일 코드로 들어온다.
 */
export const SSO_MODES = ['optional', 'required', 'except_guests'] as const
export type SsoMode = (typeof SSO_MODES)[number]
